<template>
  <transition name="modal">
    <div role="dialog" aria-modal="true"
      class="tw-fixed tw-z-[1000] tw-top-0 tw-left-0 tw-size-full tw-bg-black/50 tw-overflow-x-hidden tw-overflow-y-auto"
      :id="id" ref="modal" v-if="show">
      <div
        :class="['tw-m-3 tw-mx-auto md:tw-w-fit tw-modal-dialog tw-flex tw-items-center tw-min-h-[calc(100%-3.5rem)]', maxWidth]">
        <div v-click-outside="canCloseOutside ? { handler: close, exclude: '#cookies-policy' } : null"
          :class="['tw-flex tw-flex-col tw-w-full', customDialogClass]">
          <slot></slot>
        </div>
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import clickOutside from 'lib/workperks/directives/clickOutside';

export default defineComponent({
  name: "TwModal",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    closeOnKeyPress: {
      type: Boolean,
      default: true,
    },
    customDialogClass: {
      type: String,
      default: '',
    },
    maxWidth: {
      type: String,
      default: 'tw-max-w-xl',
    },
    canCloseOutside: {
      type: Boolean,
      default: true,
    },
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      focusableElements: 'button:not([disabled]), [href], input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])',
    }
  },
  watch: {
    show(show) {
      if (show) {
        document.body.classList.add('body-no-scroll');
        this.injectEventHandler();

        this.$nextTick(() => {
          this.initFocus();
        });
      } else {
        document.body.classList.remove('body-no-scroll');
        this.releaseEventHandler();
      }
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    handleKeyDown(e) {
      if (e.key === 'Escape' && this.closeOnKeyPress) {
        return this.close();
      }

      const isTabPressed = e.key === 'Tab' || e.keyCode === 9;

      if (!isTabPressed) {
        return;
      }

      const focusableContent = this.$refs.modal.querySelectorAll(this.focusableElements);
      const firstFocusableElement = focusableContent[0];
      const lastFocusableElement = focusableContent[focusableContent.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          lastFocusableElement.focus();
          e.preventDefault();
        }
      } else if (document.activeElement === lastFocusableElement) {
        firstFocusableElement.focus();
        e.preventDefault();
      }
    },
    injectEventHandler() {
      document.addEventListener('keydown', this.handleKeyDown);
    },
    releaseEventHandler() {
      document.removeEventListener('keydown', this.handleKeyDown);
    },
    initFocus() {
      const focusableContent = this.$refs.modal.querySelectorAll(this.focusableElements);
      const firstFocusableElement = focusableContent[0];
      firstFocusableElement.focus();
    }
  },
  directives: {
    'click-outside': clickOutside,
  },
});
</script>

<style lang="scss" scoped>
.tw-modal-dialog {
  transition: all .3s ease;
  -webkit-transform: scale(1);
  transform: scale(1);
}

/*
* The following styles are auto-applied to elements with
* transition="modal" when their visibility is toggled
* by Vue.js.
*/

.modal-enter {
  opacity: 0;
}

.modal-enter .tw-modal-dialog {
  opacity: 1;
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}

.modal-leave-active {
  opacity: 0;
}

.modal-enter .tw-modal-dialog,
.modal-leave-active .tw-modal-dialog {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}
</style>
